/**
 * Comercial Clientes — Governance (Sistema A em BRL)
 *
 * Lista clubes × subscription com counts de atletas (ativos + suspensos por quota).
 * Permite vincular novo plano (clubes sem sub), trocar plano (com preview dos NOMES
 * que seriam suspensos como safety brake) e cancelar subscription.
 *
 * Endpoints (founder via JWT super_admin):
 *   GET    /api/governance/comercial/subscriptions
 *   GET    /api/governance/comercial/subscriptions/preview-change?institution_id=X&plan_key=Y
 *   POST   /api/governance/comercial/subscriptions
 *   POST   /api/governance/comercial/subscriptions/{id}/cancel
 *
 * Disciplina UI_PADRAO:
 *   - Preto/branco/cinza por padrão.
 *   - Vermelho dessaturado (#fef2f2/#7f1d1d/#f3d1d1) APENAS no badge de quota exceeded
 *     e no modal de preview-change quando would_suspend_count > 0.
 *   - Preview modal com NOMES é safety brake (mesma disciplina do ArchiveModal da C1).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, ChevronRight, RefreshCw, AlertCircle, AlertTriangle,
  X, Link2, Edit3, XCircle, CheckCircle2,
} from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import {
  listSubscriptions, previewChange, createSubscription, cancelSubscription,
} from '../../services/comercialSubsService';
const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

// Vermelho dessaturado (UI_PADRAO) — uso EXCLUSIVO pra sinal de limite/quota
const ALERT = {
  bg:     '#fef2f2',
  text:   '#7f1d1d',
  border: '#f3d1d1',
  dot:    '#a93333',
};

// ─────────────────────────────────────────────────────────────────────────────
// i18n
// ─────────────────────────────────────────────────────────────────────────────

const LABELS = {
  pt: {
    pageTitle: 'Clientes',
    pageSubtitle: 'Sistema A — institution × subscription × atletas',
    loadingDesc: 'Carregando dados do banco…',
    clientsDesc: (n) => `${n} ${n === 1 ? 'instituição' : 'instituições'} — dados reais (Acqua/VUKZ/Minas)`,
    viewPlans: 'Ver Planos',
    refresh: 'Atualizar',
    filterAll: 'Todos',
    filterWith: 'Com plano',
    filterWithout: 'Sem plano',
    metrics: ['Instituições', 'Com plano ativo', 'Atletas suspensos', 'MRR ativo (estimado)'],
    metricNotes: ['total', 'com subscription', 'por quota excedida', 'soma de plan.price_brl'],
    emptyList: 'Nenhuma instituição para exibir.',
    noPlan: 'Sem plano vinculado',
    linkPlan: 'Vincular plano',
    quotaUsedSuffix: (active, quota) => `${active} / ${quota}`,
    suspendedBadge: (n) => `${n} ${n === 1 ? 'atleta suspenso' : 'atletas suspensos'} por quota`,
    canceledStatus: 'cancelada',
    tiles: { athletes: 'Atletas', staff: 'Staff', ai: 'Créditos AI' },
    actions: {
      change: 'Trocar plano',
      cancel: 'Cancelar',
    },
    consultLabel: 'Sob consulta',
    previewModal: {
      title: 'Trocar plano',
      titleLink: 'Vincular plano',
      currentLabel: 'Plano atual',
      targetLabel: 'Novo plano',
      selectPlaceholder: 'Selecione um plano…',
      quotaCurrent: 'Quota atual',
      quotaTarget: 'Quota nova',
      activeAthletes: 'Atletas ativos hoje',
      withinQuota: 'Tudo dentro da quota — nenhum atleta será suspenso.',
      wouldSuspendHeader: (n) => `${n} ${n === 1 ? 'atleta seria SUSPENSO' : 'atletas seriam SUSPENSOS'}:`,
      wouldSuspendNote: 'Os atletas excedentes serão suspensos automaticamente com motivo "plan_quota_exceeded". O gestor recebe um alerta no painel.',
      confirmSafe: 'Confirmar troca',
      confirmSuspend: (n) => `Confirmar e suspender ${n} ${n === 1 ? 'atleta' : 'atletas'}`,
      confirmLink: 'Vincular',
      cancel: 'Cancelar',
      loadingPreview: 'Calculando…',
      noPlanPicked: 'Escolha um plano para ver o impacto.',
      errorDefault: 'Erro ao calcular preview',
      errorSave: 'Erro ao salvar',
      saving: 'Aplicando…',
    },
    cancelModal: {
      title: 'Cancelar subscription',
      confirm: (name) => `Cancelar a subscription de "${name}"?\nO plano deixa de cobrar. Os atletas continuam no club (gestor decide depois).`,
      reasonLabel: 'Motivo (opcional)',
      reasonPlaceholder: 'Ex: cliente migrou pra Stripe direto',
      proceed: 'Cancelar subscription',
      cancel: 'Voltar',
      saving: 'Cancelando…',
      errorDefault: 'Erro ao cancelar',
    },
  },
  en: {
    pageTitle: 'Clients',
    pageSubtitle: 'System A — institution × subscription × athletes',
    loadingDesc: 'Loading database data…',
    clientsDesc: (n) => `${n} institution${n === 1 ? '' : 's'} — live data (Acqua/VUKZ/Minas)`,
    viewPlans: 'View Plans',
    refresh: 'Refresh',
    filterAll: 'All',
    filterWith: 'With plan',
    filterWithout: 'Without plan',
    metrics: ['Institutions', 'With active plan', 'Suspended athletes', 'Active MRR (est.)'],
    metricNotes: ['total', 'with subscription', 'by quota exceeded', 'sum of plan.price_brl'],
    emptyList: 'No institutions to display.',
    noPlan: 'No plan linked',
    linkPlan: 'Link plan',
    quotaUsedSuffix: (active, quota) => `${active} / ${quota}`,
    suspendedBadge: (n) => `${n} athlete${n === 1 ? '' : 's'} suspended by quota`,
    canceledStatus: 'canceled',
    tiles: { athletes: 'Athletes', staff: 'Staff', ai: 'AI credits' },
    actions: {
      change: 'Change plan',
      cancel: 'Cancel',
    },
    consultLabel: 'On request',
    previewModal: {
      title: 'Change plan',
      titleLink: 'Link plan',
      currentLabel: 'Current plan',
      targetLabel: 'New plan',
      selectPlaceholder: 'Pick a plan…',
      quotaCurrent: 'Current quota',
      quotaTarget: 'New quota',
      activeAthletes: 'Active athletes today',
      withinQuota: 'Within quota — no athlete will be suspended.',
      wouldSuspendHeader: (n) => `${n} athlete${n === 1 ? '' : 's'} would be SUSPENDED:`,
      wouldSuspendNote: 'Exceeding athletes will be auto-suspended with reason "plan_quota_exceeded". Manager gets an alert.',
      confirmSafe: 'Confirm change',
      confirmSuspend: (n) => `Confirm and suspend ${n} athlete${n === 1 ? '' : 's'}`,
      confirmLink: 'Link',
      cancel: 'Cancel',
      loadingPreview: 'Computing…',
      noPlanPicked: 'Pick a plan to see the impact.',
      errorDefault: 'Error computing preview',
      errorSave: 'Error saving',
      saving: 'Applying…',
    },
    cancelModal: {
      title: 'Cancel subscription',
      confirm: (name) => `Cancel "${name}" subscription?\nBilling stops. Athletes remain in the club (manager decides next).`,
      reasonLabel: 'Reason (optional)',
      reasonPlaceholder: 'e.g. client migrated to Stripe direct',
      proceed: 'Cancel subscription',
      cancel: 'Back',
      saving: 'Canceling…',
      errorDefault: 'Error canceling',
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const FAMILY_CONFIG = {
  basic:      { color: '#6b7280' },
  pro:        { color: BRAND_GOLD },
  enterprise: { color: '#111111' },
};

const fmtBRL = (cents, consultLabel = 'Sob consulta') => {
  if (cents == null) return consultLabel;
  const v = Number(cents) / 100;
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });
};

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR'));

// ─────────────────────────────────────────────────────────────────────────────
// ClientCard
// ─────────────────────────────────────────────────────────────────────────────

const QuotaTile = ({ label, used, quota, color }) => {
  const pct = quota > 0 ? Math.round((used / quota) * 100) : 0;
  const overQuota = used > quota;
  return (
    <div className="bg-gray-50 rounded-lg px-2 py-1.5 text-center">
      <div className="text-base font-bold" style={{ color }}>
        {fmtInt(used)}<span className="text-gray-400 font-normal">/{fmtInt(quota)}</span>
      </div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      {quota > 0 && (
        <div className="text-[9px] mt-0.5" style={{ color: overQuota ? ALERT.text : '#9ca3af' }}>
          {pct}%
        </div>
      )}
    </div>
  );
};

// Mini-tile que mostra só o limite (sem fração uso/quota) — staff_seats e ai_credits
// no payload atual não trazem "used", só "limit". Mostrar limit/0 mentiria.
const QuotaTileLimit = ({ label, quota, color }) => (
  <div className="bg-gray-50 rounded-lg px-2 py-1.5 text-center">
    <div className="text-base font-bold" style={{ color }}>{fmtInt(quota)}</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
    <div className="text-[9px] text-gray-400 mt-0.5">limite</div>
  </div>
);

const ClientCard = ({ inst, onChange, onCancel, onLink, t }) => {
  const sub = inst.sub;
  const fam = sub ? (FAMILY_CONFIG[sub.plan_family] || { color: '#6b7280' }) : { color: '#6b7280' };
  const isCanceled = sub && sub.status === 'cancelled';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 transition-all hover:shadow-md flex flex-col">
      {/* Header: instituição */}
      <div className="mb-3">
        <h3 className="text-base font-bold text-gray-900">{inst.institution_name}</h3>
        <p className="text-[11px] text-gray-500 mt-0.5 capitalize">
          {inst.institution_type || '—'}
        </p>
      </div>

      {/* Plan ou "sem plano" */}
      {!sub ? (
        <div className="bg-gray-50 rounded-lg p-3 text-center mb-3">
          <p className="text-xs text-gray-500 font-medium">{t.noPlan}</p>
        </div>
      ) : (
        <>
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: fam.color }}>
            {sub.plan_family || '—'}
            {sub.size_band && <span className="ml-1 text-gray-400">· {sub.size_band}</span>}
            {isCanceled && (
              <span className="ml-2 text-[9px] text-gray-500">({t.canceledStatus})</span>
            )}
          </div>
          <h4 className="text-sm font-bold mb-0.5" style={{ color: fam.color }}>
            {sub.plan_name}
          </h4>
          <div className="text-[11px] text-gray-500 mb-3 font-mono">{sub.plan_key}</div>

          {/* Quotas — athletes é o crítico (uso/limite); staff/AI só mostram limite */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <QuotaTile
              label={t.tiles.athletes}
              used={sub.active_athletes_count || 0}
              quota={sub.athletes_quota || 0}
              color={fam.color}
            />
            <QuotaTileLimit
              label={t.tiles.staff}
              quota={sub.staff_seats_quota || 0}
              color={fam.color}
            />
            <QuotaTileLimit
              label={t.tiles.ai}
              quota={sub.ai_credits_quota || 0}
              color={fam.color}
            />
          </div>

          {/* Alert badge — vermelho dessaturado APENAS aqui */}
          {(sub.suspended_athletes_count || 0) > 0 && (
            <div
              className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 mb-3 border"
              style={{ backgroundColor: ALERT.bg, borderColor: ALERT.border }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: ALERT.dot }}
              />
              <span className="text-[11px] font-semibold" style={{ color: ALERT.text }}>
                {t.suspendedBadge(sub.suspended_athletes_count)}
              </span>
            </div>
          )}
        </>
      )}

      {/* Footer actions */}
      <div className="mt-auto pt-3 border-t border-gray-100 flex gap-2">
        {!sub || isCanceled ? (
          <button
            onClick={() => onLink(inst)}
            className="flex-1 text-xs py-1.5 rounded-lg text-white font-bold flex items-center justify-center gap-1"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            <Link2 className="w-3 h-3" /> {t.linkPlan}
          </button>
        ) : (
          <>
            <button
              onClick={() => onChange(inst)}
              className="flex-1 text-xs py-1.5 rounded-lg border border-gray-200 hover:border-gray-400 text-gray-700 hover:text-gray-900 transition-colors flex items-center justify-center gap-1"
            >
              <Edit3 className="w-3 h-3" /> {t.actions.change}
            </button>
            <button
              onClick={() => onCancel(inst)}
              className="text-xs py-1.5 px-3 rounded-lg border border-gray-200 hover:border-gray-400 text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-1"
              title={t.actions.cancel}
            >
              <XCircle className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PreviewChangeModal — safety brake com NOMES
// ─────────────────────────────────────────────────────────────────────────────

const PreviewChangeModal = ({ inst, plans, onClose, onDone, mode, t }) => {
  // mode: 'change' | 'link'
  const m       = t.previewModal;
  const isLink  = mode === 'link';
  const sub     = inst.sub;

  const [targetKey,       setTargetKey]    = useState('');
  const [preview,         setPreview]      = useState(null);
  const [loadingPreview,  setLoading]      = useState(false);
  const [saving,          setSaving]       = useState(false);
  const [error,           setError]        = useState(null);

  // Quando muda target, busca preview
  useEffect(() => {
    if (!targetKey) {
      setPreview(null);
      return;
    }
    setLoading(true);
    setError(null);
    previewChange(inst.institution_id, targetKey)
      .then(setPreview)
      .catch((e) => {
        const msg = e?.response?.data?.error || e?.message || m.errorDefault;
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        setPreview(null);
      })
      .finally(() => setLoading(false));
  }, [targetKey, inst.institution_id, m.errorDefault]);

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await createSubscription({
        institution_id: inst.institution_id,
        plan_key: targetKey,
      });
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || m.errorSave;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const wouldSuspend = preview?.would_suspend_count || 0;
  const isDanger     = wouldSuspend > 0;

  // Filtra planos ativos
  const availablePlans = (plans || []).filter((p) => p.is_active);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{isLink ? m.titleLink : m.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{inst.institution_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current vs Target */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">{m.currentLabel}</div>
            <div className="text-sm font-bold text-gray-900">
              {sub ? sub.plan_name : '—'}
            </div>
            <div className="text-[11px] text-gray-500 font-mono mt-0.5">
              {sub ? sub.plan_key : t.noPlan}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">{m.targetLabel}</div>
            <select
              value={targetKey}
              onChange={(e) => setTargetKey(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              <option value="">{m.selectPlaceholder}</option>
              {availablePlans.map((p) => (
                <option key={p.plan_key} value={p.plan_key}>
                  {p.plan_name} ({p.plan_key})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Preview box */}
        {!targetKey ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center mb-4">
            <p className="text-xs text-gray-500">{m.noPlanPicked}</p>
          </div>
        ) : loadingPreview ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center mb-4">
            <p className="text-xs text-gray-500">{m.loadingPreview}</p>
          </div>
        ) : preview ? (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-[9px] text-gray-500 uppercase">{m.activeAthletes}</div>
                <div className="text-base font-bold text-gray-900 mt-1">
                  {fmtInt(preview.current_athletes_count)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-[9px] text-gray-500 uppercase">{m.quotaCurrent}</div>
                <div className="text-base font-bold text-gray-900 mt-1">
                  {preview.current_athletes_quota != null ? fmtInt(preview.current_athletes_quota) : '—'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-[9px] text-gray-500 uppercase">{m.quotaTarget}</div>
                <div className="text-base font-bold text-gray-900 mt-1">
                  {fmtInt(preview.target_athletes_quota)}
                </div>
              </div>
            </div>

            {wouldSuspend === 0 ? (
              <div
                className="flex items-center gap-2 rounded-lg px-3 py-2 mb-4 border bg-gray-50 border-gray-200"
              >
                <CheckCircle2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <p className="text-xs text-gray-700">{m.withinQuota}</p>
              </div>
            ) : (
              <div
                className="rounded-lg px-3 py-3 mb-4 border"
                style={{ backgroundColor: ALERT.bg, borderColor: ALERT.border }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: ALERT.text }} />
                  <p className="text-xs font-bold" style={{ color: ALERT.text }}>
                    {m.wouldSuspendHeader(wouldSuspend)}
                  </p>
                </div>
                <ul
                  className="text-[11px] space-y-0.5 pl-6 max-h-32 overflow-y-auto"
                  style={{ color: ALERT.text }}
                >
                  {(preview.would_suspend_athletes || []).slice(0, 50).map((a) => (
                    <li key={a.athlete_id}>
                      • {a.full_name || `Atleta #${a.athlete_id}`}
                    </li>
                  ))}
                  {(preview.would_suspend_athletes || []).length > 50 && (
                    <li className="italic opacity-70">
                      … +{(preview.would_suspend_athletes || []).length - 50} mais
                    </li>
                  )}
                </ul>
                <p className="text-[10px] mt-2 italic" style={{ color: ALERT.text }}>
                  {m.wouldSuspendNote}
                </p>
              </div>
            )}
          </>
        ) : null}

        {error && (
          <p className="text-xs text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg p-2">{error}</p>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">
            {m.cancel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!targetKey || saving || loadingPreview}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: isDanger ? ALERT.dot : BRAND_GREEN }}
          >
            {saving
              ? m.saving
              : isLink
                ? m.confirmLink
                : isDanger
                  ? m.confirmSuspend(wouldSuspend)
                  : m.confirmSafe}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CancelModal
// ─────────────────────────────────────────────────────────────────────────────

const CancelModal = ({ inst, onClose, onDone, t }) => {
  const m = t.cancelModal;
  const sub = inst.sub;
  const [reason, setReason] = useState('');
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);

  const handle = async () => {
    setBusy(true);
    setError(null);
    try {
      await cancelSubscription(sub.id, { reason: reason.trim() || null });
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || m.errorDefault;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-gray-900 text-lg mb-2">{m.title}</h3>
        <p className="text-sm text-gray-700 mb-3">
          <span className="font-semibold">{inst.institution_name}</span> ·{' '}
          <span className="font-mono text-xs text-gray-500">{sub?.plan_key}</span>
        </p>

        <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">
          {m.confirm(inst.institution_name)}
        </p>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">{m.reasonLabel}</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={m.reasonPlaceholder}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {error && (
          <p className="text-xs text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg p-2">{error}</p>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">
            {m.cancel}
          </button>
          <button
            onClick={handle}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-60"
            style={{ backgroundColor: '#b91c1c' }}
          >
            {busy ? m.saving : m.proceed}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const GovernanceClientesPage = () => {
  const { lang } = useGovernanceLang();
  const t        = LABELS[lang] || LABELS.pt;

  const [items,         setItems]         = useState([]);
  const [plans,         setPlans]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [filter,        setFilter]        = useState('all');  // all | with | without
  const [changingInst,  setChangingInst]  = useState(null);
  const [linkingInst,   setLinkingInst]   = useState(null);
  const [cancelingInst, setCancelingInst] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: itemsList, plans: plansList } = await listSubscriptions();
      setItems(itemsList || []);
      setPlans(plansList || []);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.error || e?.message || 'Erro ao carregar';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter
  const filtered = useMemo(() => {
    if (filter === 'with')    return items.filter((i) => i.sub && i.sub.status === 'active');
    if (filter === 'without') return items.filter((i) => !i.sub || i.sub.status === 'cancelled');
    return items;
  }, [items, filter]);

  // Metrics
  const totalInstitutions = items.length;
  const withActiveSub     = items.filter((i) => i.sub && i.sub.status === 'active').length;
  const suspendedTotal    = items.reduce(
    (s, i) => s + (i.sub?.suspended_athletes_count || 0),
    0
  );
  const mrrCentsActive = items.reduce((s, i) => {
    if (i.sub && i.sub.status === 'active' && i.sub.price_brl_cents) {
      return s + Number(i.sub.price_brl_cents);
    }
    return s;
  }, 0);

  const METRICS = [
    {
      label: t.metrics[0],
      value: loading ? '…' : String(totalInstitutions),
      note:  t.metricNotes[0],
    },
    {
      label: t.metrics[1],
      value: loading ? '…' : String(withActiveSub),
      note:  t.metricNotes[1],
    },
    {
      label: t.metrics[2],
      value: loading ? '…' : String(suspendedTotal),
      note:  t.metricNotes[2],
      alert: suspendedTotal > 0,
    },
    {
      label: t.metrics[3],
      value: loading ? '…' : fmtBRL(mrrCentsActive, t.consultLabel),
      note:  t.metricNotes[3],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <img
            src="/vukz-logo.png"
            alt="Vukz"
            className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-gray-900 text-2xl font-extrabold">{t.pageTitle}</h1>
            <p className="text-gray-500 mt-1 text-sm">
              {loading ? t.loadingDesc : t.clientsDesc(totalInstitutions)}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">{t.pageSubtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={loadAll}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            title={t.refresh}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Link
            to="/governance/comercial/planos"
            className="flex items-center gap-1 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            <Package className="w-4 h-4" /> {t.viewPlans} <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {METRICS.map((mm) => (
          <div
            key={mm.label}
            className="bg-white rounded-xl border p-4 text-center"
            style={{ borderColor: mm.alert ? ALERT.border : '#e5e7eb' }}
          >
            <div
              className="text-xl font-bold"
              style={{ color: mm.alert ? ALERT.text : BRAND_GREEN }}
            >
              {mm.value}
            </div>
            <div className="text-xs font-medium text-gray-900 mt-1">{mm.label}</div>
            <div className="text-xs text-gray-500">{mm.note}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      {!loading && items.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all',     label: `${t.filterAll} (${items.length})` },
            { key: 'with',    label: `${t.filterWith} (${withActiveSub})` },
            { key: 'without', label: `${t.filterWithout} (${items.length - withActiveSub})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filter === f.key
                  ? 'text-white border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
              style={filter === f.key ? { backgroundColor: BRAND_GREEN } : {}}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 h-64 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500 text-sm">
          {t.emptyList}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((inst) => (
            <ClientCard
              key={inst.institution_id}
              inst={inst}
              onChange={setChangingInst}
              onCancel={setCancelingInst}
              onLink={setLinkingInst}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {changingInst && (
        <PreviewChangeModal
          inst={changingInst}
          plans={plans}
          mode="change"
          onClose={() => setChangingInst(null)}
          onDone={() => { setChangingInst(null); loadAll(); }}
          t={t}
        />
      )}
      {linkingInst && (
        <PreviewChangeModal
          inst={linkingInst}
          plans={plans}
          mode="link"
          onClose={() => setLinkingInst(null)}
          onDone={() => { setLinkingInst(null); loadAll(); }}
          t={t}
        />
      )}
      {cancelingInst && (
        <CancelModal
          inst={cancelingInst}
          onClose={() => setCancelingInst(null)}
          onDone={() => { setCancelingInst(null); loadAll(); }}
          t={t}
        />
      )}
    </div>
  );
};

export default GovernanceClientesPage;
